import React, { useEffect, useState, useRef } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { locationsRes, safe } from "../../lib/locations";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Plus, Trash, MapPin, Target } from "@phosphor-icons/react";

// Fix default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const greenIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function ClickHandler({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => { if (lat && lng) map.setView([lat, lng], Math.max(map.getZoom(), 13)); }, [lat, lng, map]);
  return null;
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function DropLocations() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [locations, setLocations] = useState([]);
  const emptyForm = {
    name: "", district_id: "", lsgd_id: "", zone_ids: new Set(),
    latitude: 10.0, longitude: 76.3,
    access: "public",
    days: new Set(), start_time: "06:00", end_time: "18:00",
    schedule_type: "weekly", start_date: "",
    blocked_dates: [],
    _newBlockedDate: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const [d, z, l] = await Promise.all([
        safe(api.get("/drop-locations")), safe(api.get("/zones")), locationsRes(),
      ]);
      setItems(d.data); setZones(z.data); setLocations(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const districts = locations.filter((x) => x.type === "district");
  const lsgds = locations.filter((x) => x.type === "lsgd" && (!form.district_id || x.parent_id === form.district_id));
  const filteredZones = zones.filter((z) => !form.lsgd_id || z.lsgd_id === form.lsgd_id);

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setForm(f => ({ ...f, latitude: p.coords.latitude, longitude: p.coords.longitude })),
      () => toast.error("Could not get location"),
    );
  };

  const toggleDay = (d) => {
    const s = new Set(form.days);
    if (s.has(d)) s.delete(d); else s.add(d);
    setForm(f => ({ ...f, days: s }));
  };

  const toggleZone = (zid) => {
    const s = new Set(form.zone_ids);
    if (s.has(zid)) s.delete(zid); else s.add(zid);
    setForm(f => ({ ...f, zone_ids: s }));
  };

  const addBlockedDate = () => {
    const d = form._newBlockedDate;
    if (!d) return;
    if (form.blocked_dates.includes(d)) return;
    setForm(f => ({ ...f, blocked_dates: [...f.blocked_dates, d].sort(), _newBlockedDate: "" }));
  };
  const removeBlockedDate = (d) => {
    setForm(f => ({ ...f, blocked_dates: f.blocked_dates.filter(x => x !== d) }));
  };

  const reset = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const zoneIds = Array.from(form.zone_ids);
      const payload = {
        name: form.name,
        district_id: form.district_id || null,
        lsgd_id: form.lsgd_id || null,
        zone_ids: zoneIds,
        zone_id: zoneIds[0] || null,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        access: form.access,
        available_days: form.access === "public" ? Array.from(form.days) : [],
        start_time: form.start_time,
        end_time: form.end_time,
        schedule_type: form.schedule_type,
        start_date: form.start_date || null,
        blocked_dates: form.access === "public" ? form.blocked_dates : [],
      };
      if (editingId) {
        await api.put(`/drop-locations/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/drop-locations", payload);
        toast.success("Created");
      }
      reset();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const edit = (d) => {
    setEditingId(d.id);
    const zids = d.zone_ids && d.zone_ids.length ? d.zone_ids : (d.zone_id ? [d.zone_id] : []);
    setForm({
      name: d.name,
      district_id: d.district_id || "",
      lsgd_id: d.lsgd_id || "",
      zone_ids: new Set(zids),
      latitude: d.latitude, longitude: d.longitude,
      access: d.access || "public",
      days: new Set(d.available_days || []),
      start_time: d.start_time || "06:00", end_time: d.end_time || "18:00",
      schedule_type: d.schedule_type || "weekly",
      start_date: d.start_date || "",
      blocked_dates: d.blocked_dates || [],
      _newBlockedDate: "",
    });
  };

  const del = async (id) => {
    if (!window.confirm("Delete drop location?")) return;
    try { await api.delete(`/drop-locations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Drop Locations" subtitle="Master Control">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
            <div className="h-[420px]" data-testid="drop-map">
              <MapContainer center={[form.latitude, form.longitude]} zoom={12} style={{ height: "100%", width: "100%" }}>
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <ClickHandler onPick={(lat, lng) => setForm(f => ({ ...f, latitude: lat, longitude: lng }))} />
                <Recenter lat={form.latitude} lng={form.longitude} />
                <Marker position={[form.latitude, form.longitude]} icon={greenIcon} />
                {items.map(d => (
                  <Marker key={d.id} position={[d.latitude, d.longitude]} />
                ))}
              </MapContainer>
            </div>
          </div>
          <div className="bg-white border border-[#E5E2D9] rounded-md">
            <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center justify-between">
              <div className="font-heading text-lg">All drop locations</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">{items.length} total</div>
            </div>
            <table className="w-full text-sm" data-testid="drops-table">
              <thead>
                <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Access</th>
                  <th className="px-6 py-3">Zones</th>
                  <th className="px-6 py-3">GPS</th>
                  <th className="px-6 py-3">Schedule</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-[#8A8780]">No drop locations.</td></tr>}
                {items.map((d, i) => {
                  const zcount = (d.zone_ids && d.zone_ids.length) || (d.zone_id ? 1 : 0);
                  const access = d.access || "public";
                  return (
                    <tr key={d.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                      <td className="px-6 py-3 font-medium">{d.name}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${access === "private" ? "bg-[#D6DDE6] text-[#1E3A5F]" : "bg-[#D4E0D1] text-[#1A3317]"}`}>
                          {access}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-[#5C5A55] text-xs">{zcount} zone{zcount === 1 ? "" : "s"}</td>
                      <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">
                        {d.latitude.toFixed(5)}, {d.longitude.toFixed(5)}
                      </td>
                      <td className="px-6 py-3 text-[#5C5A55] text-xs">
                        {access === "private" ? "—" : (
                          <>
                            {d.start_time} – {d.end_time}
                            {d.schedule_type === "alternate_week" && <span className="ml-1 text-[10px] text-[#7A5A11]">alt</span>}
                            {(d.blocked_dates && d.blocked_dates.length > 0) && <span className="ml-1 text-[10px] text-[#7A2E18]">· {d.blocked_dates.length} blocked</span>}
                          </>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <button onClick={() => edit(d)} data-testid={`drop-edit-${d.id}`} className="text-[#2D5A27] text-xs mr-3">Edit</button>
                        <button onClick={() => del(d.id)} data-testid={`drop-del-${d.id}`} className="text-[#C25D38] text-xs inline-flex items-center gap-1"><Trash size={12} />Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">{editingId ? "Edit drop point" : "New drop point"}</div>
          <div className="font-heading text-lg mb-4 flex items-center gap-2"><MapPin size={18} color="#2D5A27" weight="fill" /> Click map to pick GPS</div>
          <form onSubmit={save} className="space-y-3" data-testid="drop-form">
            <input data-testid="drop-name" required placeholder="Name" value={form.name}
                   onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
            <select data-testid="drop-district" value={form.district_id}
                    onChange={(e) => setForm(f => ({ ...f, district_id: e.target.value, lsgd_id: "", zone_id: "" }))}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
              <option value="">— select district —</option>
              {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select data-testid="drop-lsgd" value={form.lsgd_id}
                    onChange={(e) => setForm(f => ({ ...f, lsgd_id: e.target.value, zone_ids: new Set() }))}
                    disabled={!form.district_id}
                    className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2]">
              <option value="">{form.district_id ? "— select LSGD —" : "— select district first —"}</option>
              {lsgds.map((l) => <option key={l.id} value={l.id}>{l.name}{l.lsgd_type ? ` (${l.lsgd_type.replace("_", " ")})` : ""}</option>)}
            </select>

            {/* Multi-zone picker */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
                Health zones {form.zone_ids.size > 0 && <span className="normal-case tracking-normal text-[#2D5A27]">· {form.zone_ids.size} selected</span>}
              </div>
              {!form.lsgd_id ? (
                <div className="text-xs text-[#8A8780] h-9 flex items-center px-2">Select LSGD first</div>
              ) : filteredZones.length === 0 ? (
                <div className="text-xs text-[#8A8780] h-9 flex items-center px-2">No zones for this LSGD</div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="drop-zones">
                  {filteredZones.map(z => {
                    const on = form.zone_ids.has(z.id);
                    return (
                      <button type="button" key={z.id} onClick={() => toggleZone(z.id)}
                              data-testid={`drop-zone-${z.id}`}
                              className={`h-8 px-3 rounded-md text-xs border transition-colors ${on ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2]"}`}>
                        {z.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="drop-lat" type="number" step="any" required placeholder="Latitude" value={form.latitude}
                     onChange={(e) => setForm(f => ({ ...f, latitude: e.target.value }))}
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono text-sm" />
              <input data-testid="drop-lng" type="number" step="any" required placeholder="Longitude" value={form.longitude}
                     onChange={(e) => setForm(f => ({ ...f, longitude: e.target.value }))}
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md font-mono text-sm" />
            </div>
            <button type="button" onClick={useCurrentLocation}
                    className="w-full h-9 text-xs border border-[#E5E2D9] rounded-md inline-flex items-center justify-center gap-2 hover:bg-[#F7F6F2]">
              <Target size={12} weight="bold" /> Use my location
            </button>

            {/* Access toggle */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Access</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="drop-access-public"
                        onClick={() => setForm(f => ({ ...f, access: "public" }))}
                        className={`h-10 rounded-md text-sm border transition-colors ${form.access === "public" ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                  Public · customers can drop
                </button>
                <button type="button" data-testid="drop-access-private"
                        onClick={() => setForm(f => ({ ...f, access: "private" }))}
                        className={`h-10 rounded-md text-sm border transition-colors ${form.access === "private" ? "bg-[#D6DDE6] border-[#1E3A5F] text-[#1E3A5F]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                  Private · staff godown only
                </button>
              </div>
              <div className="text-[11px] text-[#8A8780] mt-1">
                {form.access === "public"
                  ? "Visible to customers; schedule and holidays apply."
                  : "Staff-only. No customer-facing schedule; omit below settings."}
              </div>
            </div>

            {/* Schedule (only for public) */}
            {form.access === "public" && (
              <>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Available days</div>
                  <div className="grid grid-cols-7 gap-1">
                    {DAYS.map(d => (
                      <button type="button" key={d} data-testid={`drop-day-${d}`}
                              onClick={() => toggleDay(d)}
                              className={`h-9 text-xs font-medium rounded border transition-colors ${form.days.has(d) ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="drop-start" type="time" value={form.start_time}
                         onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
                  <input data-testid="drop-end" type="time" value={form.end_time}
                         onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Schedule type</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="drop-sched-weekly"
                            onClick={() => setForm(f => ({ ...f, schedule_type: "weekly" }))}
                            className={`h-10 rounded-md text-sm border transition-colors ${form.schedule_type === "weekly" ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                      Weekly
                    </button>
                    <button type="button" data-testid="drop-sched-alt"
                            onClick={() => setForm(f => ({ ...f, schedule_type: "alternate_week" }))}
                            className={`h-10 rounded-md text-sm border transition-colors ${form.schedule_type === "alternate_week" ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                      Alternate week
                    </button>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
                    Anchor start date {form.schedule_type === "alternate_week" && <span className="text-[#C25D38] normal-case tracking-normal">· required</span>}
                  </div>
                  <input data-testid="drop-start-date" type="date" value={form.start_date}
                         required={form.schedule_type === "alternate_week"}
                         onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))}
                         className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
                  <div className="text-[11px] text-[#8A8780] mt-1">
                    Reference date used to compute week-parity for alternate-week schedules.
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">
                    Blocked dates {form.blocked_dates.length > 0 && <span className="normal-case tracking-normal text-[#7A2E18]">· {form.blocked_dates.length}</span>}
                  </div>
                  <div className="flex gap-1">
                    <input data-testid="drop-blocked-input" type="date"
                           value={form._newBlockedDate}
                           onChange={(e) => setForm(f => ({ ...f, _newBlockedDate: e.target.value }))}
                           className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md" />
                    <button type="button" onClick={addBlockedDate} data-testid="drop-blocked-add"
                            className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">Add</button>
                  </div>
                  {form.blocked_dates.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="drop-blocked-list">
                      {form.blocked_dates.map(d => (
                        <span key={d} className="inline-flex items-center gap-1 h-7 px-2 bg-[#F4C7B9]/40 border border-[#F4C7B9] rounded text-[11px] text-[#7A2E18]">
                          {d}
                          <button type="button" onClick={() => removeBlockedDate(d)} className="hover:text-[#C25D38]">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="text-[11px] text-[#8A8780] mt-1">Holidays / maintenance days — customers can't drop here.</div>
                </div>
              </>
            )}
            <div className="flex gap-2 pt-2">
              <button data-testid="drop-save" type="submit"
                      className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <Plus size={14} weight="bold" />{editingId ? "Update" : "Save drop point"}
              </button>
              {editingId && <button type="button" onClick={reset} className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>}
            </div>
          </form>
        </div>
      </div>
    </AppShell>
  );
}
